// Core UI pieces and helpers
import BaseTable from 'components/Tables/BaseTable';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';

// Contexts + React
import { useAppSSE } from 'contexts/AppSSEContext';
import { usePerms } from 'contexts/PermsContext';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

// Data fetching + services
import { useGetWorkplaceMembersQuery } from 'services/organization/organization.api';
import { deleteUser } from 'services/user/user.service';

// Selectors, utils and toasts
import { selectUsername } from 'store/selectors/User/userSelectors';
import { getVisibleUsers } from 'utils/users/role.utils';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import {
  filterUsersBySearchTerm,
  mapWorkplaceMembersToUserRows,
  normalizeRowIds,
  userHasSharedHierarchyOrHigher,
} from './utils/userTable.utils';

// Local components/hooks for user-specific cells and actions
import DeleteUserModal from '../components/DeleteUserModal';
import { GenericUserCell } from './components/GenericUserCell';
import UserActionsCell from './components/UserActionsCell';
import { UserNameCell } from './components/UserNameCell';
import UserRolesCell from './components/UserRolesCell';
import { useRoleHierarchy } from './hooks/useRoleHierarchy';
import type { UserRow, UserTableProps } from './types/UserTable.types';
import { mergeCustomStyles, mergeMuiProps } from './utils/style.utils';
// Presentational cells are small, testable components located in ./components

/**
 * UserTable
 *
 * Centralized, reusable user listing component for Admin features.
 *
 * Usage modes:
 *  - Controlled rows: pass `rows` (array of `UserRow`) and optional `columns` to render
 *    a specific dataset (useful when the page has already fetched and normalized rows).
 *  - Uncontrolled / fetch mode: omit `rows` and the component will fetch workplace members
 *    via `useGetWorkplaceMembersQuery` using the current permissions context. This is the
 *    default behavior used by `MembersTab`.
 *
 * Key props and behaviors:
 *  - `rowKey` (default `userId`): field name to use as the unique id for a row. `BaseTable`
 *    expects an `id` on each row; `UserTable` maps `row[rowKey]` to `id` when needed.
 *  - `columns`: override or extend the default columns provided by `UserTable`.
 *  - Selection and highlighting: pass `selectedRowId` or `selectedRowIds` and `onRowClick`
 *    to enable the highlight-on-click behavior. These props are forwarded to `BaseTable`.
 *  - Presentation helpers: `UserTable` exposes presentational cells and the delete modal
 *    (e.g. `UserActionsCell`, `UserRolesCell`, `DeleteUserModal`) so callers can reuse
 *    them during migration and customization.
 *
 * Examples:
 *  - MembersTab (default): <UserTable /> // fetches members automatically
 *  - DetailsTab modal: <UserTable rows={modalRows} selectedRowIds={toSelect} onRowClick={...} />
 *  - Custom list: <UserTable rows={rows} columns={[...]} rowKey="userId" />
 */
const UserTable: FC<UserTableProps> = ({
  rows,
  perms,
  onView,
  onEdit,
  onDeleteSuccess,
  rowKey = 'userId',
  renderToolbar,
  searchTerm,
  columns: columnsProp,
  selectedRowIds,
  selectedRowId,
  onRowClick,
  preserveSelectedRowOnHover = false,
  customEmptyMessage,
  customStyles: customStylesProp,
  muiTablePaperProps: muiPaperProps,
  muiTableContainerProps: muiContainerProps,
}) => {
  const permsContext = usePerms();
  const permsToUse = perms ?? permsContext;
  const username = useSelector(selectUsername);
  // NOTE: permsToUse is the effective permission set applied to the UserTable.

  const { allRoles, hierarchy, yourHighestRoleHierarchyId } = useRoleHierarchy();
  // useRoleHierarchy maps role -> hierarchy level and gives the caller's highest role id

  const shouldFetchMembers = typeof rows === 'undefined';
  // shouldFetchMembers is true when caller did not pass `rows` and the component should fetch

  const {
    data: workplaceMembersFetched = [],
    isFetching: workplaceMembersFetching,
    refetch: refetchWorkplaceMembers,
  } = useGetWorkplaceMembersQuery(getVisibleUsers(permsToUse));
  // Fetch workplace members when uncontrolled; scoped by getVisibleUsers(permsToUse)

  // Delete state — modal + service call handled internally
  const [pendingDeleteUser, setPendingDeleteUser] = useState<UserRow | null>(null);

  const handleDeleteRequest = (user: UserRow) => setPendingDeleteUser(user);

  const handleDeleteConfirm = async () => {
    if (!pendingDeleteUser) return;
    try {
      await deleteUser(pendingDeleteUser.userId);
      notifySuccessToast('User', { isAddOperation: false, isDeleteOperation: true });
      setPendingDeleteUser(null);
      refetchWorkplaceMembers();
      onDeleteSuccess?.();
    } catch {
      notifyErrorToast('User', { isAddOperation: false, isDeleteOperation: true });
    }
  };

  const handleDeleteCancel = () => setPendingDeleteUser(null);

  // Live refresh: the backend emits a `user-roles-updated` SSE event (one per
  // affected user) whenever a user's role membership changes. Refetch the
  // authoritative members list rather than trusting any pushed role data. A
  // single bulk edit can fire several events back-to-back, so debounce to
  // collapse the burst into one refetch. Only active in uncontrolled/fetch mode.
  // useAppSSE lives in an untyped .jsx (createContext(null)), so its return type
  // collapses to `never` here — annotate the shape we rely on.
  const { subscribe } = useAppSSE() as {
    subscribe: (eventType: string, callback: (data: unknown) => void) => () => void;
  };
  const refetchRef = useRef(refetchWorkplaceMembers);
  refetchRef.current = refetchWorkplaceMembers;

  useEffect(() => {
    if (!shouldFetchMembers) return undefined;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = subscribe('user-roles-updated', () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        refetchRef.current?.();
      }, 300);
    });

    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [shouldFetchMembers, subscribe]);

  const rowsFromApi = useMemo(() => {
    return mapWorkplaceMembersToUserRows(workplaceMembersFetched, {
      username,
      hierarchy,
      yourHighestRoleHierarchyId,
      permsToUse,
    });
  }, [workplaceMembersFetched, username, hierarchy, yourHighestRoleHierarchyId, permsToUse]);

  const effectiveRows: UserRow[] = useMemo(() => {
    if (shouldFetchMembers) return rowsFromApi;
    const source = Array.isArray(rows) ? rows : [];
    return source.map((r) => {
      let shared = r.sharedHierarchyOrHigher;
      if (typeof shared === 'undefined') {
        try {
          shared = userHasSharedHierarchyOrHigher(
            r.roles,
            hierarchy,
            yourHighestRoleHierarchyId,
            permsToUse,
          );
        } catch {
          shared = false;
        }
      }
      return { ...r, sharedHierarchyOrHigher: shared };
    });
  }, [shouldFetchMembers, rowsFromApi, rows, hierarchy, yourHighestRoleHierarchyId, permsToUse]);
  // effectiveRows: either caller-provided rows (controlled) or API-derived rows (uncontrolled)

  const effectiveRowsFiltered = useMemo(
    () => filterUsersBySearchTerm(effectiveRows, searchTerm),
    [effectiveRows, searchTerm],
  );

  // defaultColumns: feature-level defaults; callers may override via `columns` prop
  const defaultColumns = useMemo(() => {
    return [
      {
        header: 'Users',
        accessorKey: 'name',
        Cell: ({ row, cell }: any) => (
          <UserNameCell value={cell.getValue()} activated={row.original.activated} />
        ),
        size: 200,
      },
      {
        header: 'Roles',
        accessorKey: 'role_name',
        Cell: ({ row, cell }: any) => <UserRolesCell roles={cell.getValue()} allRoles={allRoles} />,
      },
      {
        header: 'Email',
        accessorKey: 'email',
        Cell: ({ row, cell }: any) => <GenericUserCell value={cell.getValue()} />,
        size: 200,
      },
      {
        header: 'Created By',
        accessorKey: 'create_by_email',
        Cell: ({ row, cell }: any) => <GenericUserCell value={cell.getValue()} />,
        size: 200,
      },
      {
        header: 'Date Created',
        accessorKey: 'date_added',
        Cell: ({ row, cell }: any) => <GenericUserCell value={cell.getValue()} />,
      },
      {
        header: '',
        accessorKey: 'Settings',
        Cell: ({ row }: any) => (
          <UserActionsCell
            user={row.original}
            perms={permsToUse}
            currentUsername={username}
            onView={onView}
            onEdit={onEdit}
            onDelete={handleDeleteRequest}
          />
        ),
        enableSorting: false,
      },
    ];
  }, [allRoles, permsToUse, onView, onEdit, username]);

  // columnsToUse: use `columns` prop when provided, otherwise use defaultColumns
  const columnsToUse = useMemo(
    () => (Array.isArray(columnsProp) && columnsProp.length ? columnsProp : defaultColumns),
    [columnsProp, defaultColumns],
  );

  // rowsWithId: ensure each row has a stable `id` (rowKey || userId || id)
  const rowsWithId = useMemo(
    () => normalizeRowIds(effectiveRowsFiltered, rowKey),
    [effectiveRowsFiltered, rowKey],
  );

  // mergedCustomStyles: defaults merged with caller-provided customStyles
  const mergedCustomStyles = mergeCustomStyles(
    {
      container: { flex: 1, minHeight: 0 },
      cell: { borderBottom: '3px solid #E0E1E228' },
      header: { borderBottom: '3px solid #E0E1E228' },
    },
    customStylesProp,
  );

  // mergedMuiPaperProps: sensible MUI Paper defaults for table layout
  const mergedMuiPaperProps = mergeMuiProps(
    { sx: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } },
    muiPaperProps,
  );

  // mergedMuiContainerProps: defaults to make the table scrolling area flexible
  const mergedMuiContainerProps = mergeMuiProps(
    { sx: { flex: 1, overflow: 'auto' } },
    muiContainerProps,
  );

  // Show loading animation on initial uncontrolled fetch when no rows yet
  if (shouldFetchMembers && workplaceMembersFetching && effectiveRows.length === 0) {
    return <WidgetLoadingAnimation widgetName="Charts" dimensions={[1, 1]} disableText={true} />;
  }

  return (
    <div className="w-100 flex-grow-1 overflow-hidden d-flex flex-column" style={{ minHeight: 0 }}>
      {/* Modal to confirm deleting a user — controlled by `pendingDeleteUser` */}
      <DeleteUserModal
        user={pendingDeleteUser}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
      {renderToolbar && <div className="mb-3">{renderToolbar}</div>}
      {/* BaseTable: forwards selection/highlight props (selectedRowIds, selectedRowId, onRowClick) */}
      <BaseTable
        columns={columnsToUse}
        rows={rowsWithId}
        customStyles={mergedCustomStyles}
        muiTablePaperProps={mergedMuiPaperProps}
        muiTableContainerProps={mergedMuiContainerProps}
        selectedRowIds={selectedRowIds}
        selectedRowId={selectedRowId}
        onRowClick={onRowClick}
        preserveSelectedRowOnHover={preserveSelectedRowOnHover}
        customEmptyMessage={customEmptyMessage}
      />
    </div>
  );
};

export default UserTable;
